import React, {useCallback, useMemo} from 'react';
import {Button, DropdownMenu, Icon, Spin, TableColumnConfig, Text} from '@gravity-ui/uikit';
import {ArrowRightArrowLeft, CaretDown, CaretUp, Pencil, TrashBin} from '@gravity-ui/icons';
import {DataTable} from '../../shared';
import {CategoryInGroupModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import styles from './CategoryGroupCategoriesTable.module.scss';

type SortProperty = 'name' | 'order';
type SortOrder = 'ascending' | 'descending';

interface CategoryGroupCategoriesTableProps {
  categories: CategoryInGroupModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  sortProperty?: SortProperty;
  sortOrder?: SortOrder;
  onSortChange?: (property: SortProperty | undefined, order: SortOrder | undefined) => void;
  onEditCategory?: (category: CategoryInGroupModel) => void;
  onDeleteCategory?: (category: CategoryInGroupModel) => void;
  onMoveCategory?: (category: CategoryInGroupModel) => void;
}

export const CategoryGroupCategoriesTable = ({
  categories,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sortProperty,
  sortOrder = 'ascending',
  onSortChange,
  onEditCategory,
  onDeleteCategory,
  onMoveCategory,
}: CategoryGroupCategoriesTableProps) => {
  const SKELETON_ROWS = 5;

  const handleEdit = useCallback(
    (category: CategoryInGroupModel) => {
      onEditCategory?.(category);
    },
    [onEditCategory],
  );

  const handleMove = useCallback(
    (category: CategoryInGroupModel) => {
      onMoveCategory?.(category);
    },
    [onMoveCategory],
  );

  const handleDelete = useCallback(
    (category: CategoryInGroupModel) => {
      onDeleteCategory?.(category);
    },
    [onDeleteCategory],
  );

  const handleSort = useCallback(
    (property: SortProperty) => {
      if (!onSortChange) return;
      if (property === sortProperty) {
        const newOrder = sortOrder === 'ascending' ? 'descending' : 'ascending';
        onSortChange(property, newOrder);
      } else {
        onSortChange(property, 'ascending');
      }
    },
    [sortProperty, sortOrder, onSortChange],
  );

  const handleResetSort = useCallback(() => {
    onSortChange?.(undefined, undefined);
  }, [onSortChange]);

  const renderSortIcon = useCallback(
    (property: SortProperty) => {
      if (property !== sortProperty) {
        return <Icon data={CaretDown} className={styles.sortIconInactive} />;
      }
      return sortOrder === 'ascending' ? (
        <Icon data={CaretUp} className={styles.sortIconActive} />
      ) : (
        <Icon data={CaretDown} className={styles.sortIconActive} />
      );
    },
    [sortProperty, sortOrder],
  );

  const columns = useMemo<TableColumnConfig<CategoryInGroupModel>[]>(
    () => [
      {
        id: 'name',
        name: () => (
          <div className={styles.sortableHeader} onClick={() => handleSort('name')}>
            <Text variant="body-1">Название категории</Text>
            {renderSortIcon('name')}
          </div>
        ),
        template: (item: CategoryInGroupModel) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{item.name}</Text>
          </div>
        ),
      },
      {
        id: 'order',
        name: () => (
          <div className={styles.sortableHeader} onClick={() => handleSort('order')}>
            <Text variant="body-1">Порядок показа</Text>
            {renderSortIcon('order')}
          </div>
        ),
        template: (item: CategoryInGroupModel) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{item.order}</Text>
          </div>
        ),
      },
      {
        id: 'more',
        name: () => (
          <div className={styles.tableHeader}>
            <Button view="normal" size="m" onClick={handleResetSort}>
              <Text variant="body-2">Сбросить сортировку</Text>
            </Button>
          </div>
        ),
        template: (item: CategoryInGroupModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={Pencil} />,
                  action: () => handleEdit(item),
                  text: 'Редактировать',
                  className: styles.dropdownItem,
                },
                {
                  iconStart: <Icon size={16} data={ArrowRightArrowLeft} />,
                  action: () => handleMove(item),
                  text: 'Переместить',
                  className: styles.dropdownItem,
                },
                ...(item.usageCount === 0
                  ? [
                      {
                        iconStart: <Icon size={16} data={TrashBin} />,
                        action: () => handleDelete(item),
                        text: 'Удалить',
                        className: styles.deleteButton,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ),
      },
    ],
    [handleSort, renderSortIcon, handleResetSort, handleEdit, handleMove, handleDelete],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<CategoryInGroupModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={categories || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Категории не найдены"
        emptyStateDescription="Категория с таким названием не найдена. Попробуйте изменить запрос"
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
